import { useState } from "react";

interface LangContent {
  code: string;
  label: string;
  nav: string[];
  hero: string;
  cta: string;
}

const LANGUAGES: LangContent[] = [
  {
    code: "PT",
    label: "Português",
    nav: ["Início", "Serviços", "Empresa", "Contato"],
    hero: "Bem-vindo à sua empresa",
    cta: "Fale conosco",
  },
  {
    code: "EN",
    label: "English",
    nav: ["Home", "Services", "Company", "Contact"],
    hero: "Welcome to your company",
    cta: "Contact us",
  },
  {
    code: "ES",
    label: "Español",
    nav: ["Inicio", "Servicios", "Empresa", "Contacto"],
    hero: "Bienvenido a su empresa",
    cta: "Contáctenos",
  },
];

/**
 * Demonstração interativa de troca de idioma: mostra onde o visitante muda
 * o idioma no site (seletor no topo) e o conteúdo se adaptando.
 */
export default function LanguageSwitcher() {
  const [active, setActive] = useState(0);
  const lang = LANGUAGES[active];

  return (
    <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:gap-12">
      <div>
        <span className="inline-flex items-center gap-2 rounded-full bg-accent-100 px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-accent-700">
          <i className="ri-cursor-line" aria-hidden="true"></i>
          Onde muda o idioma
        </span>
        <h3 className="mt-4 font-heading text-xl font-bold tracking-tight text-foreground-950 md:text-2xl">
          Um seletor de idioma no topo do site.
        </h3>
        <p className="mt-3 text-sm leading-relaxed text-foreground-600">
          O visitante troca entre <strong>português, inglês e espanhol</strong> com um clique, e o
          conteúdo do site aparece no idioma escolhido — sem sair da página.
        </p>

        <div
          role="tablist"
          aria-label="Selecionar idioma"
          className="mt-6 inline-flex items-center gap-1 rounded-full border border-accent-200 bg-background-50 px-1 py-1"
        >
          {LANGUAGES.map((item, index) => (
            <button
              key={item.code}
              type="button"
              role="tab"
              aria-selected={active === index}
              onClick={() => setActive(index)}
              className={`inline-flex items-center gap-2 whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                active === index
                  ? "bg-accent-500 text-background-50"
                  : "text-foreground-600 hover:bg-accent-50"
              }`}
            >
              <span className="font-heading text-xs">{item.code}</span>
              {item.label}
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-background-200 bg-background-50">
        <div className="flex items-center gap-2 border-b border-background-200 bg-background-100 px-4 py-3">
          <span className="h-2.5 w-2.5 rounded-full bg-foreground-300"></span>
          <span className="h-2.5 w-2.5 rounded-full bg-foreground-200"></span>
          <span className="h-2.5 w-2.5 rounded-full bg-foreground-200"></span>
        </div>

        <div className="p-5 md:p-6">
          <div className="flex items-center justify-between border-b border-background-200 pb-4">
            <div className="flex items-center gap-2">
              <span className="h-6 w-6 rounded-md bg-gradient-to-br from-primary-500 to-accent-500"></span>
              <span className="font-heading text-sm font-bold text-foreground-950">Sua Empresa</span>
            </div>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-accent-300 bg-accent-50 px-3 py-1 text-[11px] font-semibold text-accent-700">
              <i className="ri-translate-2" aria-hidden="true"></i>
              {lang.code}
              <i className="ri-arrow-down-s-line" aria-hidden="true"></i>
            </span>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs font-medium text-foreground-500">
            {lang.nav.map((n) => (
              <span key={n}>{n}</span>
            ))}
          </div>

          <div className="mt-6 rounded-xl bg-background-100 p-5">
            <p
              key={lang.hero}
              className="font-heading text-lg font-bold text-foreground-950 md:text-xl animate-fadeIn"
            >
              {lang.hero}
            </p>
            <p className="mt-2 text-xs leading-relaxed text-foreground-600">
              {lang.label} — o mesmo site, no idioma do seu cliente.
            </p>
            <span className="mt-4 inline-flex items-center gap-2 rounded-md bg-primary-500 px-4 py-2 text-xs font-semibold text-background-50">
              {lang.cta}
              <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}